import { faDownload } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu } from "@headlessui/react";
import { toKML } from "@placemarkio/tokml";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { geoJSONToWkt } from "betterknown";
import type { FeatureCollection } from "geojson";
import { useEffect, useRef, useState } from "react";
import { GeoFilterArea } from "../../../filters/GeoFilterSelect";
import { useDrawAndFilterControlsV2 } from "../../hooks/areaFilter";
import { ControlContainer } from "../common";

interface DownloadDropdownProps {
    drawnShape: any;
    className?: string;
}

export const DownloadDropdown = ({
    drawnShape,
    className = "",
}: DownloadDropdownProps) => {
    const [, copyToClipboard] = useCopyToClipboard();

    const copyWKTString = () => {
        if (drawnShape.features.length > 0) {
            const wkt = geoJSONToWkt(drawnShape.features[0].geometry);
            copyToClipboard(wkt);
        }
    };

    const downloadGeoJson = () => {
        const dataStr =
            "data:text/json;charset=utf-8," +
            encodeURIComponent(JSON.stringify(drawnShape.features[0].geometry));
        const downloadAnchorNode = document.createElement("a");
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute("download", "selection.geojson");
        document.body.appendChild(downloadAnchorNode);
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    };

    const downloadKML = () => {
        const dataStr =
            "data:application/vnd.google-earth.kml+xml;charset=utf-8," +
            encodeURIComponent(toKML(drawnShape));
        const downloadAnchorNode = document.createElement("a");
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute("download", "selection.kml");
        document.body.appendChild(downloadAnchorNode);
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    };

    const menuItems = [
        { label: "Download KML", onClick: downloadKML },
        { label: "Download GeoJSON", onClick: downloadGeoJson },
        { label: "Copy WKT string", onClick: copyWKTString },
    ];

    return (
        <Menu
            as="div"
            className={`relative inline-block text-left ${className}`}
        >
            <div>
                <Menu.Button className="flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded">
                    Download
                    <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
                </Menu.Button>
            </div>
            <Menu.Items className="absolute left-0 mt-2 w-36 text-xs origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                <div className="px-1 py-1">
                    {menuItems.map((item) => (
                        <Menu.Item key={item.label}>
                            {({ active }) => (
                                <button
                                    type="button"
                                    className={`${
                                        active
                                            ? "bg-ae-blue-50 text-ae-blue-550"
                                            : "text-gray-900"
                                    } group flex w-full items-center rounded-md px-2 py-0.5`}
                                    onClick={item.onClick}
                                >
                                    {item.label}
                                </button>
                            )}
                        </Menu.Item>
                    ))}
                </div>
            </Menu.Items>
        </Menu>
    );
};

export const AreasFilterControl = () => {
    const {
        selectedFilters,
        setPresets,
        loadingPresets,
        drawingMode,
        setShape,
        startDrawing,
        isDrawing,
        stopDrawing,
        commitShape,
        drawnShape,
        filterData,
    } = useDrawAndFilterControlsV2("mainMap");

    // Pending shape: drawn (drawingMode === "EDIT") or uploaded, awaiting confirmation.
    const [pendingShape, setPendingShape] = useState<FeatureCollection>();
    // Track whether the pending shape originated from drawing (vs upload).
    const pendingIsDrawn = useRef(false);

    // When the user closes a drawn polygon (DRAW → EDIT), move it into pending.
    // Don't trigger for uploaded shapes (pendingIsDrawn guards against re-entry).
    useEffect(() => {
        if (
            isDrawing &&
            drawingMode === "EDIT" &&
            (drawnShape?.features.length ?? 0) > 0 &&
            !pendingIsDrawn.current &&
            !pendingShape
        ) {
            pendingIsDrawn.current = true;
            setPendingShape(drawnShape as FeatureCollection);
        }
    }, [isDrawing, drawingMode, drawnShape, pendingShape]);

    const committedShape: FeatureCollection | undefined =
        filterData && (drawnShape?.features.length ?? 0) > 0
            ? (drawnShape as FeatureCollection)
            : undefined;

    return (
        <ControlContainer className="p-0.5 w-fit">
            <div className="w-[400px]">
                <GeoFilterArea
                    selectedPresets={selectedFilters}
                    setSelectedPresets={setPresets}
                    loadingPresets={loadingPresets}
                    allowDownload
                    combinedShape={drawnShape as FeatureCollection}
                    allowCustomShape
                    customShape={committedShape}
                    setCustomShape={(shape) => {
                        if (shape) {
                            // Mark as no longer drawn-pending before setPendingShape(undefined)
                            // fires, so the setPendingShape wrapper doesn't call stopDrawing(false).
                            pendingIsDrawn.current = false;
                            commitShape(shape);
                        } else {
                            stopDrawing(false);
                        }
                    }}
                    pendingShape={pendingShape}
                    setPendingShape={(shape) => {
                        setPendingShape(shape);
                        if (shape && !pendingIsDrawn.current) {
                            // Uploaded shape — push it into the hook so the map renders it.
                            setShape(shape);
                        } else if (!shape && pendingIsDrawn.current) {
                            // Cancelled a drawn pending shape — cancel the drawing session.
                            pendingIsDrawn.current = false;
                            stopDrawing(false);
                        }
                    }}
                    allowDrawing
                    startDrawing={startDrawing}
                    isDrawing={isDrawing && drawingMode === "DRAW"}
                />
            </div>
        </ControlContainer>
    );
};
